import { logError } from "@lib/logger";
import { sharedStyles } from "@lib/sharedStyles";
import {
	BarController,
	BarElement,
	CategoryScale,
	Chart,
	Legend,
	LinearScale,
	Title,
	Tooltip
} from "chart.js";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";

Chart.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	BarController
);

@customElement("bar-chart")
export class BarChart extends LitElement {
	static readonly styles = [
		sharedStyles,
		css`
		.chart-container {
			width: 100%;
			height: 300px;
			position: relative;
		}
		
		#myChart {
			height: 100%;
			margin: 8px;
		}
		`
	];

	@property({ type: Object }) barChartProps;
	private chart: Chart | null = null;

	constructor(barChartProps) {
		super();
		this.barChartProps = barChartProps;
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this._resetChart();
	}

	firstUpdated() {
		this._renderChart(this.barChartProps);
	}

	updated(changedProperties: Map<string, any>) {
		if (changedProperties.has('barChartProps')) {
			this._renderChart(this.barChartProps);
		}
	}

	private _renderChart(barChartProps) {
		try {
			const canvas = this._getCanvas(this.shadowRoot);
			this._resetChart();
			const ctx = this._getChartContext(canvas);
			this.chart = this._createChart(ctx, barChartProps);
		} catch (error) {
			alert('Failed to create chart');
			logError('Failed to create chart:', error);
		}
	}

	private _getCanvas(shadowRoot: ShadowRoot) {
		const canvas = shadowRoot?.querySelector('#myChart') as HTMLCanvasElement;
		if (!canvas) {
			throw new Error('Canvas element not found');
		}
		return canvas;
	}

	private _resetChart() {
		if (this.chart) {
			this.chart.destroy();
			this.chart = null;
		}
	}

	private _getChartContext(canvas: HTMLCanvasElement) {
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			throw new Error('Could not get 2D context');
		}
		return ctx;
	}

	private _createChart(ctx: CanvasRenderingContext2D, barChartProps) {
		return new Chart(ctx, {
			type: 'bar',
			data: {
				labels: barChartProps.labels,
				datasets: barChartProps.datasets
			},
			options: barChartProps.options
		});
	}

	render() {
		return html`
			<div style="display: flex; flex-direction: column; gap: 16px; align-items: center;">
			<h1>${this.barChartProps.title}</h1>
			<div class="chart-container">
			<canvas id="myChart"></canvas>
			</div>
			</div>
		`;
	}
}
export default BarChart;